/*
 * NextGenn engine · the hero loop (sections/ngs-hero.liquid).
 *
 * The hero arrives as ONE PHOTOGRAPH, which is the LCP: it is in the markup,
 * eager, and nothing waits on it. This element lays a short silent loop over
 * that photograph once the loop can actually play, and fades it in so the
 * change is a breath rather than a cut. If any of that fails the photograph is
 * simply what the hero is, which is why the film is never in the HTML.
 *
 * The same care as the scroll film, for the same reasons:
 *
 *  · The file is asked for only after the page has loaded and the visitor
 *    has touched, scrolled or moved the mouse, so it never competes with the
 *    photograph for the first screen.
 *  · A visitor who asked to save data, or is on 2G, or asked for less motion,
 *    keeps the photograph and is never charged for the film.
 *  · The loop is dropped the moment it leaves the screen and taken up again on
 *    return: a hero film that keeps decoding while somebody reads the page
 *    three screens down is a battery bill with nothing to show for it.
 *  · `play()` is a promise that a phone may refuse (Low Power Mode). It is
 *    tried on load, again on the first touch, and if it never lands the
 *    photograph stays. The film NEVER becomes visible unless it is running.
 */
if (!customElements.get('ngs-hero-film')) {
  customElements.define(
    'ngs-hero-film',
    class NgsHeroFilm extends HTMLElement {
      /* Two to three megabytes of film is not ours to spend uninvited. */
      static saving() {
        const c = navigator.connection;
        return Boolean(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
      }

      connectedCallback() {
        this.video = this.querySelector('video');
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.wide = window.matchMedia('(min-width: 750px)');
        if (!this.video || !this.source()) return;
        if (this.reduced.matches || NgsHeroFilm.saving()) return;

        this.live = true;
        this.current = '';
        this.prime = this.prime.bind(this);
        this.onReduced = () => {
          if (this.reduced.matches) this.drop();
        };
        this.onBreak = () => {
          if (this.current) this.attach();
        };
        this.onPlaying = () => this.setAttribute('data-ready', '');
        this.onError = () => this.drop();

        this.video.addEventListener('playing', this.onPlaying);
        this.video.addEventListener('error', this.onError);
        if (this.reduced.addEventListener) this.reduced.addEventListener('change', this.onReduced);
        if (this.wide.addEventListener) this.wide.addEventListener('change', this.onBreak);

        /* Only once the page has finished with the things it owes the first
           screen, AND somebody is there: the first touch, scroll or mouse
           move (window.ngsIntent, layout/theme.liquid). Until then the hero is
           the photograph, which is what a speed test and a slow phone see.
           `load` has already fired on a back button, so ask twice. */
        const loaded =
          document.readyState === 'complete'
            ? Promise.resolve()
            : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
        Promise.all([loaded, window.ngsIntent || Promise.resolve()]).then(() => this.begin());
      }

      disconnectedCallback() {
        this.teardown();
      }

      source() {
        const d = this.dataset;
        return (this.wide && this.wide.matches ? d.wide : d.tall) || d.wide || d.tall || '';
      }

      begin() {
        if (!this.live) return;
        this.watcher = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) this.attach();
          else this.pause();
        });
        this.watcher.observe(this);
      }

      attach() {
        const src = this.source();
        if (!src) return this.drop();
        if (src !== this.current) {
          this.current = src;
          this.video.src = src;
          this.video.load();
        }
        this.play();
      }

      play() {
        const attempt = this.video.play();
        if (attempt && typeof attempt.then === 'function') {
          attempt.catch(() => {
            /* Refused: the first touch is the permission, and until then the
               photograph is the hero. */
            window.addEventListener('touchstart', this.prime, { passive: true, once: true });
          });
        }
      }

      prime() {
        if (this.live && this.current) this.play();
      }

      pause() {
        if (this.current) this.video.pause();
      }

      teardown() {
        if (!this.live) return;
        this.live = false;
        if (this.watcher) this.watcher.disconnect();
        this.video.removeEventListener('playing', this.onPlaying);
        this.video.removeEventListener('error', this.onError);
        if (this.reduced.removeEventListener) this.reduced.removeEventListener('change', this.onReduced);
        if (this.wide.removeEventListener) this.wide.removeEventListener('change', this.onBreak);
        window.removeEventListener('touchstart', this.prime);
      }

      /* Back to the photograph, and stop paying for the film. */
      drop() {
        this.teardown();
        this.removeAttribute('data-ready');
        if (this.current) {
          this.current = '';
          this.video.pause();
          this.video.removeAttribute('src');
          this.video.load();
        }
      }
    }
  );
}
